import React, { useCallback, useEffect, useState } from 'react';
import { Copy, Download, FileArchive, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../context/AdminContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  title_en: '',
  title_ar: '',
  description_en: '',
  description_ar: '',
  slug: '',
  category: '',
  password: '',
  clear_password: false,
  is_active: true,
  file: null,
};

const formatSize = (bytes = 0) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / (1024 ** index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const slugify = (value) => value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const AdminDownloads = () => {
  const { toast } = useToast();
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const loadDownloads = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/downloads');
      setDownloads(response.data);
    } catch {
      toast({ title: 'Error', description: 'Failed to load downloads', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadDownloads(); }, [loadDownloads]);

  const resetForm = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const openCreate = () => {
    resetForm();
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      ...emptyForm,
      title_en: item.title?.en || '',
      title_ar: item.title?.ar || '',
      description_en: item.description?.en || '',
      description_ar: item.description?.ar || '',
      slug: item.slug,
      category: item.category || '',
      is_active: item.is_active,
    });
    setOpen(true);
  };

  const saveDownload = async (event) => {
    event.preventDefault();
    if (!form.title_en || !form.slug || (!editing && !form.file)) {
      toast({ title: 'Error', description: 'Title, slug, and file are required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = new FormData();
      ['title_en', 'title_ar', 'description_en', 'description_ar', 'slug', 'category', 'password'].forEach((field) => {
        payload.append(field, form[field] || '');
      });
      payload.append('is_active', form.is_active);
      payload.append('clear_password', form.clear_password);
      if (form.file) payload.append('file', form.file);

      if (editing) {
        await adminApi.put(`/downloads/${editing.id}`, payload);
        toast({ title: 'Success', description: 'Download updated' });
      } else {
        await adminApi.post('/downloads', payload);
        toast({ title: 'Success', description: 'Download created' });
      }
      setOpen(false);
      resetForm();
      loadDownloads();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to save download';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const deleteDownload = async (id) => {
    if (!window.confirm('Delete this download?')) return;
    try {
      await adminApi.delete(`/downloads/${id}`);
      toast({ title: 'Deleted', description: 'Download removed' });
      loadDownloads();
    } catch {
      toast({ title: 'Error', description: 'Failed to delete download', variant: 'destructive' });
    }
  };

  const copyLink = (slug) => {
    const url = `${window.location.origin}/downloads/${slug}`;
    navigator.clipboard.writeText(url);
    toast({ title: 'Copied', description: url });
  };

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="h-8 w-8 animate-spin text-red-600" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Download Manager</h1>
          <p className="text-gray-500 text-sm mt-1">Upload files and share public download links with optional passwords.</p>
        </div>
        <Button className="bg-red-600 hover:bg-red-700" onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />Add Download
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(value) => { if (!value) resetForm(); setOpen(value); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Download' : 'Add Download'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveDownload} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Title (English) *</Label>
                <Input value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value, slug: editing ? form.slug : slugify(e.target.value) })} required />
              </div>
              <div>
                <Label>Title (Arabic)</Label>
                <Input value={form.title_ar} onChange={(e) => setForm({ ...form, title_ar: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Slug *</Label>
              <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} disabled={!!editing} required />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Description (English)</Label>
                <Input value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} />
              </div>
              <div>
                <Label>Description (Arabic)</Label>
                <Input value={form.description_ar} onChange={(e) => setForm({ ...form, description_ar: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="APK, Manual, Update" />
              </div>
              <div>
                <Label>{editing ? 'Replace file' : 'File *'}</Label>
                <Input type="file" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} required={!editing} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <Label>{editing ? 'New password' : 'Password'}</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value, clear_password: false })} placeholder="Optional" />
              </div>
              {editing && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.clear_password} onChange={(e) => setForm({ ...form, clear_password: e.target.checked, password: '' })} />
                  Remove password
                </label>
              )}
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                Active
              </label>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="bg-red-600 hover:bg-red-700" disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editing ? 'Update' : 'Create'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent className="pt-6">
          {downloads.length === 0 ? (
            <p className="text-center text-gray-400 py-8">No downloads yet</p>
          ) : (
            <div className="space-y-3">
              {downloads.map((item) => (
                <div key={item.id} className={`flex flex-col md:flex-row md:items-center justify-between gap-3 rounded-lg border p-4 ${item.is_active ? 'bg-white' : 'bg-gray-50 opacity-60'}`}>
                  <div className="flex gap-3 min-w-0">
                    <div className="h-11 w-11 rounded-lg bg-red-50 text-red-600 flex items-center justify-center shrink-0">
                      <FileArchive className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900">{item.title?.en || item.slug}</div>
                      <div className="text-sm text-gray-500 truncate">{item.original_filename} · {formatSize(item.file_size)} · /downloads/{item.slug}</div>
                      <div className="text-xs text-gray-400">{item.requires_password ? 'Password protected' : 'Public'} · {item.download_count || 0} downloads</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 self-end md:self-auto">
                    <Button variant="ghost" size="sm" onClick={() => copyLink(item.slug)}><Copy className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" asChild><a href={`${BACKEND_URL}/api/downloads/${item.slug}/file`} target="_blank" rel="noreferrer"><Download className="h-4 w-4" /></a></Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(item)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => deleteDownload(item.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDownloads;
